import { observer } from 'mobx-react';

import { useFormikContext } from 'formik';
import { VirtualOffice } from 'types/firm';
import { ValidState, VirtualOfficeFormValues } from 'types/firmInfo';

import { makeStyles } from '@bequestinc/wui';
import {
  Collapse,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import { useValidStatesQuery } from 'hooks/query-hooks/address/useValidStatesQuery';

import { RADIUS_OPTIONS } from 'utils/constants';

import ZipField from 'components/custom_fields/ZipField';

const useStyles = makeStyles()({
  textField: {
    width: '100%',
  },
  radiusTextField: {
    width: '150px',
  },
});

interface PropTypes {
  virtualOffice: Partial<VirtualOffice>;
  canUpdate: boolean;
}

const VirtualOfficeGeographicParameters = ({ virtualOffice, canUpdate }: PropTypes) => {
  const { values, setFieldValue } = useFormikContext<VirtualOfficeFormValues>();
  const { classes } = useStyles();
  const { data: US_STATES = [] } = useValidStatesQuery() as { data?: ValidState[] };

  const hasFieldsUnderReview = Object.keys(virtualOffice.fieldsUnderReview).length > 0;

  return (
    <RadioGroup
      name="geographicParameterType"
      value={values.geographicParameterType}
      onChange={(_event, value) => {
        setFieldValue('geographicParameterType', value);
      }}
    >
      <FormControlLabel
        value="entireState"
        control={<Radio />}
        label="For the entire state of"
        disabled={hasFieldsUnderReview || !canUpdate}
      />

      <Collapse
        in={values.geographicParameterType === 'entireState' || hasFieldsUnderReview || !canUpdate}
      >
        <Grid item sx={{ ml: 4, mt: 1 }}>
          <TextField
            select
            name="state"
            label="State"
            value={values.state}
            onChange={e => setFieldValue('state', e.target.value)}
            className={classes.textField}
            disabled={hasFieldsUnderReview || !canUpdate}
            helperText={
              virtualOffice.fieldsUnderReview.state
                ? `change to '${virtualOffice.fieldsUnderReview.state}' is under review`
                : ''
            }
          >
            {US_STATES.map(state => (
              <MenuItem key={state.abbreviation} value={state.abbreviation}>
                {state.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Collapse>

      <FormControlLabel
        value="radius"
        control={<Radio />}
        label="At a radius of"
        sx={{ mt: 2 }}
        disabled={hasFieldsUnderReview || !canUpdate}
      />
      <Collapse
        in={values.geographicParameterType === 'radius' || hasFieldsUnderReview || !canUpdate}
      >
        <Grid container spacing={1} sx={{ ml: 2, mt: 0.5 }} direction="row">
          <Grid item xs={4}>
            <TextField
              select
              name="radius"
              label="Radius"
              value={values.radius ?? ''}
              onChange={e => setFieldValue('radius', e.target.value)}
              className={classes.radiusTextField}
              inputProps={{
                step: '1',
                min: '0',
              }}
              disabled={hasFieldsUnderReview || !canUpdate}
            >
              {RADIUS_OPTIONS.map(radius => (
                <MenuItem key={radius.value} value={radius.value}>
                  {radius.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={2}>
            <Typography sx={{ pt: 2 }}>from</Typography>
          </Grid>
          <Grid item xs={4}>
            <ZipField
              name="zipCode"
              label="Zip Code"
              value={values.zipCode}
              onChange={e => setFieldValue('zipCode', e.target.value)}
              disabled={hasFieldsUnderReview || !canUpdate}
            />
            <Typography variant="caption" color="text.disabled">
              {virtualOffice.fieldsUnderReview.zipCode
                ? `change to '${virtualOffice.fieldsUnderReview.zipCode}' is under review`
                : ''}
            </Typography>
          </Grid>
        </Grid>
      </Collapse>
    </RadioGroup>
  );
};

export default observer(VirtualOfficeGeographicParameters);
